import React, { useState, useRef, useCallback } from 'react';

/** Default outer (base) and inner (knob) joystick image URLs (from public folder). */
export const JOYSTICK_IMAGES = {
  outer: '/joystick-base.svg',
  inner: '/joystick-knob.svg',
} as const;

export interface JoystickProps {
  /** Label shown above the joystick */
  label?: string;
  /** Callback when stick position changes. Normalized X/Y in [-1, 1]. */
  onMove?: (x: number, y: number) => void;
  /** Alias for onMove. Both are called if provided. */
  onChange?: (x: number, y: number) => void;
  /** Container diameter in pixels. Knob scale is derived from this. */
  size?: number;
  /** Image URL for the outer/base part of the joystick. If not set, uses default SVG. */
  outerImage?: string;
  /** Image URL for the inner/knob part of the joystick. If not set, uses default SVG. */
  innerImage?: string;
  /** Output scale. 1 = full range; 0.5 = half sensitivity. Applied to normalized values. */
  sensitivity?: number;
  /** When true, show X/Y values below the joystick (DEBUG mode). */
  debug?: boolean;
}

const DEFAULT_SIZE = 160;
const KNOB_RATIO = 25 / 80; // knob radius as fraction of half-size (80 = 160/2)
/** Base SVG hole radius (viewBox 160) – overlay hides this when joystick is in use */
const HOLE_RATIO = 35 / 160;

/**
 * Reusable joystick component.
 * - Pointer capture for smooth drag (no document listeners → no duplicate events/jitter).
 * - Circular clamp; normalized output [-1, 1]; returns to center on release.
 */
const Joystick: React.FC<JoystickProps> = ({
  label,
  onMove,
  onChange,
  size = DEFAULT_SIZE,
  outerImage = JOYSTICK_IMAGES.outer,
  innerImage = JOYSTICK_IMAGES.inner,
  sensitivity = 1,
  debug = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [displayValues, setDisplayValues] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [knobImageFailed, setKnobImageFailed] = useState(false);

  const halfSize = size / 2;
  const knobRadius = halfSize * KNOB_RATIO;
  const maxDistance = halfSize - knobRadius;
  const holeRadius = size * HOLE_RATIO; // center hole in base image
  const isInUse = isDragging || position.x !== 0 || position.y !== 0;

  const notify = useCallback(
    (normalizedX: number, normalizedY: number) => {
      const sx = Math.max(-1, Math.min(1, normalizedX * sensitivity));
      const sy = Math.max(-1, Math.min(1, normalizedY * sensitivity));
      if (onMove) onMove(sx, sy);
      if (onChange) onChange(sx, sy);
    },
    [sensitivity, onMove, onChange]
  );

  const calculatePosition = useCallback(
    (clientX: number, clientY: number) => {
      if (!containerRef.current) return null;

      const rect = containerRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      let dx = clientX - centerX;
      let dy = clientY - centerY;

      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance > maxDistance && maxDistance > 0) {
        const angle = Math.atan2(dy, dx);
        dx = Math.cos(angle) * maxDistance;
        dy = Math.sin(angle) * maxDistance;
      }

      const normalizedX = maxDistance === 0 ? 0 : dx / maxDistance;
      const normalizedY = maxDistance === 0 ? 0 : dy / maxDistance;

      return {
        x: dx,
        y: dy,
        normalizedX: Math.round(normalizedX * 100) / 100,
        normalizedY: Math.round(normalizedY * 100) / 100,
      };
    },
    [maxDistance]
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      containerRef.current?.setPointerCapture(e.pointerId);
      setIsDragging(true);

      const result = calculatePosition(e.clientX, e.clientY);
      if (!result) return;

      setPosition({ x: result.x, y: result.y });
      setDisplayValues({ x: result.normalizedX, y: result.normalizedY });
      notify(result.normalizedX, result.normalizedY);
    },
    [calculatePosition, notify]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.buttons === 0) return;
      e.preventDefault();

      const result = calculatePosition(e.clientX, e.clientY);
      if (!result) return;

      setPosition({ x: result.x, y: result.y });
      setDisplayValues({ x: result.normalizedX, y: result.normalizedY });
      notify(result.normalizedX, result.normalizedY);
    },
    [calculatePosition, notify]
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      containerRef.current?.releasePointerCapture(e.pointerId);
      setIsDragging(false);

      setPosition({ x: 0, y: 0 });
      setDisplayValues({ x: 0, y: 0 });
      notify(0, 0);
    },
    [notify]
  );

  const handlePointerCancel = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      setPosition({ x: 0, y: 0 });
      setDisplayValues({ x: 0, y: 0 });
      notify(0, 0);
    },
    [notify]
  );

  return (
    <div className="flex flex-col items-center gap-3">
      {label && (
        <p className="text-xs font-semibold text-gray-300 uppercase tracking-widest">{label}</p>
      )}

      <div
        ref={containerRef}
        className="rounded-full cursor-grab active:cursor-grabbing select-none"
        style={{
          position: 'relative',
          width: size,
          height: size,
          overflow: 'visible',
          boxShadow: isDragging ? '0 0 20px rgba(2, 132, 199, 0.8)' : '0 0 10px rgba(2, 132, 199, 0.3)',
          transition: isDragging ? 'none' : 'box-shadow 0.2s',
          touchAction: 'none',
          userSelect: 'none',
          WebkitUserSelect: 'none',
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
      >
        {/* Outer part – base image, behind knob (z-index 0) */}
        <img
          src={outerImage}
          alt=""
          role="presentation"
          className="pointer-events-none block"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: size,
            height: size,
            objectFit: 'contain',
            objectPosition: 'center center',
            zIndex: 0,
          }}
          draggable={false}
        />

        {/* When joystick is in use, hide the base's center (background knob) with a dark overlay */}
        {isInUse && (
          <div
            role="presentation"
            style={{
              position: 'absolute',
              left: halfSize - holeRadius,
              top: halfSize - holeRadius,
              width: holeRadius * 2,
              height: holeRadius * 2,
              borderRadius: '50%',
              backgroundColor: 'rgb(3, 7, 18)',
              pointerEvents: 'none',
              zIndex: 5,
            }}
          />
        )}

        {/* Inner part – only ONE knob: image when it loads, otherwise CSS circle (avoids double knob / stuck knob) */}
        {knobImageFailed ? (
          <div
            role="presentation"
            style={{
              position: 'absolute',
              width: knobRadius * 2,
              height: knobRadius * 2,
              left: halfSize + position.x - knobRadius,
              top: halfSize + position.y - knobRadius,
              borderRadius: '50%',
              backgroundColor: '#0284c7',
              boxShadow: isDragging
                ? '0 0 15px rgba(2, 132, 199, 1), inset 0 0 10px rgba(255, 255, 255, 0.2)'
                : '0 0 10px rgba(2, 132, 199, 0.6)',
              transition: isDragging ? 'none' : 'left 0.15s ease-out, top 0.15s ease-out',
              pointerEvents: 'none',
              zIndex: 10,
            }}
          />
        ) : (
          <img
            src={innerImage}
            alt=""
            role="presentation"
            className="absolute pointer-events-none block"
            style={{
              position: 'absolute',
              width: knobRadius * 2,
              height: knobRadius * 2,
              left: halfSize + position.x - knobRadius,
              top: halfSize + position.y - knobRadius,
              objectFit: 'contain',
              filter: isDragging ? 'drop-shadow(0 0 8px rgba(2, 132, 199, 0.9))' : 'none',
              transition: isDragging ? 'none' : 'left 0.15s ease-out, top 0.15s ease-out',
              pointerEvents: 'none',
              zIndex: 10,
            }}
            draggable={false}
            onError={() => setKnobImageFailed(true)}
          />
        )}
      </div>

      {debug && (
        <div className="text-center">
          <div className="text-xs text-gray-500 font-mono">
            X: <span className="text-primary-400 font-bold">{displayValues.x.toFixed(2)}</span>
          </div>
          <div className="text-xs text-gray-500 font-mono">
            Y: <span className="text-primary-400 font-bold">{displayValues.y.toFixed(2)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Joystick;
